import BubbleText from "@/components/BubbleText"
import { t } from "i18next"
import Card from "@/components/card"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import Space from "@/components/space"
import Button from "@/components/button"
import SaveToast from "@/components/SaveToast"
import { faCheckCircle, faCircleNotch, faRotateRight } from "@fortawesome/free-solid-svg-icons"
import { useNavigate, useLocation } from "react-router-dom"
import unitStore from "../../unit/model/unit.store"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import RiskListItemDetail from "./RiskListItemDetail"
import ListButton from "@/components/list-button"
import Tooltip from "@/components/tooltip"
import { useState } from "react"

interface Props {
  riskType?: "phy" | "rps"
  loading?: boolean
}

export function RisksAddFromAssistant({ riskType = "phy", loading = false }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)

  const unit = useAppSelector(unitStore.selectors.active)
  const risks = useAppSelector(riskStore.selectors.list)
  const selectedRisks = useAppSelector(riskStore.selectors.listSelected)

  const status = useAppSelector(riskStore.selectors.status)

  const handleReroll = async () => {
    if (!unit) return
    setIsLoading(true)
    dispatch(riskStore.actions.setList([]))
    dispatch(riskStore.actions.clearSelected())
    try {
      const suggestedRisks = await dispatch(
        riskStore.thunks.suggestForUnit({
          ...unit,
          rps: riskType === "rps"
        })
      ).unwrap()
      dispatch(riskStore.actions.setList(suggestedRisks))
    } catch (err) {
      console.error("❌ Erreur lors de la génération des suggestions:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelect = (riskId: string) => {
    // Si le risque est déjà sélectionné, le désélectionner
    if (selectedRisks.some((r) => r.id === riskId)) {
      dispatch(riskStore.actions.clearSelected())
    } else {
      // Sinon, déselectionner tous les autres et sélectionner celui-ci
      dispatch(riskStore.actions.clearSelected())
      dispatch(riskStore.actions.select(riskId))
    }
  }

  const handleSave = async () => {
    if (!unit) return

    const { backTo } = (location.state as any) || {}

    try {
      const risk = selectedRisks[0]
      const createdRisk = await dispatch(
        riskStore.thunks.createEntity({
          ...risk,
          options: { askSave: true },
          UnitId: unit.id,
          rps: riskType === "rps"
        })
      ).unwrap()

      if (createdRisk?.id) {
        navigate(`/risks/risk/${createdRisk.id}`, { state: { backTo, showSuccessModal: true } })
      } else {
        navigate(backTo || -1)
      }
    } catch (err) {
      console.error("❌ Erreur lors de la sauvegarde des risques :", err)
    }
  }

  return (
    <div className="h-full">
      <div className="flex justify-between">
        <Space />
        <div className="flex -mt-32">
          <div className="max-w-[500px] h-[100px] mr-8 mt-auto mb-4 text-2xl">
            <BubbleText
              text={`${t(`Voici des suggestions de risques`)} ${riskType.toUpperCase()} ${t(`adaptées à votre unité`)}`}
              orientation="right"
            />
          </div>
          <img
            src={require("@/assets/assistant/owl_female.png")}
            alt="owl assistant"
            className="object-cover object-top scale-x-[-1]"
            style={{ width: 200, height: 180 }}
          />
        </div>
      </div>

      <Card
        className="overflow-hidden"
        title={
          <div className="flex justify-between items-center w-full px-[30px] pt-[20px] mt-[0.75rem]">
            <span>{`${t(`Sélection des risques`)} (${riskType.toUpperCase()})`}</span>
            <div className="flex items-center gap-4">
              <span className="text-sm grey-t font-normal">{t("Vous pouvez sélectionner les risques 1 par 1")}</span>
              {!isLoading && !loading && (
                <Tooltip text="Générer de nouvelles suggestions" position="left">
                  <ListButton icon={faRotateRight} onClick={handleReroll} />
                </Tooltip>
              )}
            </div>
          </div>
        }
        isWithoutPadding>
        <div className="card-content-list px-[30px]">
          {risks.length > 0 ? (
            <div className="mt-4">
              {risks.map((risk) => {
                const isActive = selectedRisks.some((r) => r.id === risk.id)
                return (
                  <RiskListItemDetail
                    key={risk.id}
                    risk={risk}
                    onSelect={handleSelect}
                    isSelected={isActive}
                    showCheckbox={true}
                    hideFirstAttribute={false}
                  />
                )
              })}
            </div>
          ) : (
            <div className="flex justify-center">
              <FontAwesomeIcon className="w-12 h-12 mb-6" icon={faCircleNotch} spin />
            </div>
          )}
        </div>
      </Card>

      {selectedRisks.length > 0 && (
        <SaveToast isSaveActive status={status} onCancel={() => dispatch(riskStore.actions.select([]))}>
          <Button className="primary" icon={faCheckCircle} onClick={handleSave} isWithBorder>
            {t("Continuer")}
          </Button>
        </SaveToast>
      )}
    </div>
  )
}
